import React, { useMemo, useState } from 'react';
import { checkoutAuthHeaders } from '../checkout/checkoutAuthHeaders';

function formatMoney(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

const RETURN_STATUS_LABEL = {
  requested: 'Under review',
  approved: 'Approved — ship items back',
  received: 'Received — refund pending',
  rejected: 'Not approved',
  refunded: 'Refunded',
  closed: 'Closed',
};

function windowLabel(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 1) return 'any time after payment';
  return `${n} day${n === 1 ? '' : 's'} from payment`;
}

function ReturnStatusBadge({ status }) {
  const st = String(status || '').toLowerCase();
  const cls = `customer-order-returns__status customer-order-returns__status--${st || 'default'}`;
  return <span className={cls}>{RETURN_STATUS_LABEL[st] || status}</span>;
}

function lineRefundUnit(it, detail) {
  const est = Number(it.refund_unit_estimate);
  if (Number.isFinite(est) && est >= 0) return est;
  const q = Math.max(1, Number(it.quantity) || 1);
  const lt = Number(it.line_total);
  let unit = Number.isFinite(lt) && lt > 0 ? lt / q : Number(it.unit_price) || 0;
  const sub = Number(detail?.subtotal) || 0;
  const disc = Number(detail?.discount_amount) || 0;
  if (sub > 0 && disc > 0) {
    unit *= Math.max(0, (sub - disc) / sub);
  }
  return unit;
}

export default function AccountOrderReturnRequest({
  detail,
  onOrderUpdated,
  setFeedback,
  readOnly = false,
  guestContact = '',
}) {
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const policy = detail?.returns_policy;
  const items = detail?.items || [];
  const existingReturns = Array.isArray(detail?.returns) ? detail.returns : [];

  const returnableLines = useMemo(
    () => items.filter((it) => it.can_request_return && (it.quantity_returnable || 0) > 0),
    [items]
  );

  const openReturn = useMemo(
    () =>
      existingReturns.find((r) =>
        ['requested', 'approved', 'received'].includes(String(r.status || '').toLowerCase())
      ),
    [existingReturns]
  );

  const ineligibleLines = useMemo(() => {
    if (!policy?.enabled) return [];
    return items.filter((it) => !it.can_request_return && (it.quantity || 0) > 0);
  }, [items, policy?.enabled]);

  if (!policy?.enabled) {
    return null;
  }

  if (readOnly && existingReturns.length === 0) {
    return null;
  }

  const parseQty = (n) => {
    const v = parseInt(n, 10);
    return Number.isFinite(v) ? v : 0;
  };

  const lineTotal = returnableLines.reduce((sum, it) => {
    if (!selected[it.id]) return sum;
    const q = Math.min(parseQty(selected[it.id]), parseQty(it.quantity_returnable));
    return sum + lineRefundUnit(it, detail) * q;
  }, 0);

  const toggleLine = (itemId, maxQty, on) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (!on) {
        delete next[itemId];
        return next;
      }
      next[itemId] = String(prev[itemId] || maxQty || 1);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const lineItems = returnableLines
      .filter((it) => selected[it.id])
      .map((it) => ({
        order_item_id: it.id,
        quantity: Math.min(parseQty(selected[it.id]), parseQty(it.quantity_returnable)),
      }))
      .filter((l) => l.quantity > 0);

    if (lineItems.length === 0) {
      setFeedback?.({ kind: 'error', text: 'Select at least one item to return.' });
      return;
    }
    if (!reason.trim()) {
      setFeedback?.({ kind: 'error', text: 'Please describe why you are returning these items.' });
      return;
    }

    const contact = String(guestContact || '').trim();
    const isGuest = contact.length > 0;

    setSubmitting(true);
    setFeedback?.(null);
    try {
      const body = {
        action: isGuest ? 'guest_request_return' : 'request_return',
        order_number: detail.order_number,
        reason: reason.trim(),
        line_items: lineItems,
      };
      if (isGuest) {
        if (contact.includes('@')) body.email = contact;
        else body.phone = contact;
      }
      const r = await fetch('/api/orders.php', {
        method: 'POST',
        headers: isGuest ? { 'Content-Type': 'application/json' } : checkoutAuthHeaders(),
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) {
        setFeedback?.({ kind: 'error', text: d.error || 'Could not submit return request.' });
        return;
      }
      setFeedback?.({
        kind: 'success',
        text: d.message || `Return ${d.return_number || ''} submitted. We will review it shortly.`,
      });
      setReason('');
      setSelected({});
      if (d.order && onOrderUpdated) {
        onOrderUpdated(d.order);
      }
    } catch (err) {
      setFeedback?.({ kind: 'error', text: err.message || 'Network error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="customer-order-returns" aria-labelledby="customer-order-returns-title">
      <h3 id="customer-order-returns-title" className="customer-order-returns__title">
        Returns
      </h3>

      {existingReturns.length > 0 ? (
        <ul className="customer-order-returns__history list-unstyled">
          {existingReturns.map((ret) => (
            <li key={ret.return_number} className="customer-order-returns__history-card">
              <div className="customer-order-returns__history-head">
                <span className="customer-order-returns__ref">{ret.return_number}</span>
                <ReturnStatusBadge status={ret.status} />
              </div>
              <p className="customer-order-returns__history-meta small text-muted mb-1">
                {ret.created_at ? new Date(ret.created_at).toLocaleString('en-IN') : ''}
                {ret.refund_amount > 0 ? ` · Refund ${formatMoney(ret.refund_amount)}` : ''}
              </p>
              {(ret.items || []).length > 0 ? (
                <ul className="small mb-0 ps-3">
                  {ret.items.map((it) => (
                    <li key={`${ret.return_number}-${it.order_item_id}`}>
                      {it.product_name} × {it.quantity}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {readOnly ? null : openReturn ? (
        <p className="customer-order-returns__notice small mb-0" role="status">
          You have a return in progress ({openReturn.return_number}). We will update you by email or
          WhatsApp when it is reviewed.
        </p>
      ) : returnableLines.length > 0 ? (
        <form className="customer-order-returns__form" onSubmit={onSubmit}>
          <p className="customer-order-returns__lead small text-muted">
            Select items to return. Eligible {windowLabel(policy.window_days)}.
          </p>
          <ul className="customer-order-returns__lines list-unstyled">
            {returnableLines.map((it) => {
              const max = parseQty(it.quantity_returnable);
              const isOn = Boolean(selected[it.id]);
              return (
                <li
                  key={it.id}
                  className={`customer-order-returns__line${isOn ? ' is-selected' : ''}`}
                >
                  <label className="customer-order-returns__line-check">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={(e) => toggleLine(it.id, max, e.target.checked)}
                    />
                    <span className="customer-order-returns__line-info">
                      <span className="customer-order-returns__line-name">{it.product_name}</span>
                      <span className="customer-order-returns__line-hint text-muted">
                        Up to {max} · {formatMoney(lineRefundUnit(it, detail))} each
                          {Number(detail?.discount_amount) > 0 ? ' (after coupon)' : ''}
                      </span>
                    </span>
                  </label>
                  {isOn ? (
                    <label className="customer-order-returns__qty">
                      <span className="visually-hidden">Quantity for {it.product_name}</span>
                      <input
                        type="number"
                        min={1}
                        max={max}
                        className="form-control form-control-sm"
                        value={selected[it.id] ?? max}
                        onChange={(e) =>
                          setSelected((prev) => ({ ...prev, [it.id]: e.target.value }))
                        }
                      />
                    </label>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <label className="form-label small" htmlFor={`return-reason-${detail.order_number}`}>
            Why are you returning?
          </label>
          <textarea
            id={`return-reason-${detail.order_number}`}
            className="form-control form-control-sm customer-order-returns__reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Wrong item, damaged on arrival, size issue…"
            required
          />
          {lineTotal > 0 ? (
            <p className="customer-order-returns__estimate small mb-2">
              Estimated refund (items): <strong>{formatMoney(lineTotal)}</strong>
              {detail.razorpay_payment_id ? (
                <span className="text-muted">
                  {' '}
                  — refunded to your original payment method after we approve and receive the items.
                </span>
              ) : (
                <span className="text-muted">
                  {' '}
                  — we will arrange refund by UPI or bank after approval.
                </span>
              )}
            </p>
          ) : null}
          <button
            type="submit"
            className="btn btn-primary btn-sm rounded-pill"
            disabled={submitting || Object.keys(selected).length === 0}
          >
            {submitting ? 'Submitting…' : 'Submit return request'}
          </button>
        </form>
      ) : readOnly ? null : ineligibleLines.length > 0 ? (
        <div className="customer-order-returns__ineligible">
          <p className="small text-muted mb-2">
            No items on this order are eligible for return right now.
          </p>
          <ul className="list-unstyled small mb-0">
            {ineligibleLines.slice(0, 6).map((it) => (
              <li key={it.id} className="customer-order-returns__ineligible-item">
                <span>{it.product_name}</span>
                <span className="text-muted">
                  {it.return_policy?.label || 'Not returnable'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="small text-muted mb-0">All items on this order have already been returned.</p>
      )}
    </section>
  );
}
